import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import SectionHeading from '../ui/SectionHeading';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Button from '../ui/Button';

const Services = () => {
  const navigate = useNavigate();

  const openCalendly = (e) => {
    if (e) e.preventDefault();
    if (window.Calendly) {
      window.Calendly.initPopupWidget({url: 'https://calendly.com/prboost7/30-minutes-meeting'});
    }
  };

  const services = [
    {
      title: "Guest Posts",
      desc: "Get featured on trusted websites in your niche and strengthen your brand authority",
      icon: FiIcons.FiEdit,
      color: "from-blue-500 to-cyan-400",
      border: "border-blue-500/30",
      link: "/services/guest-posts"
    },
    {
      title: "Press Releases",
      desc: "Distribute your brand across premium media sites and increase credibility",
      icon: FiIcons.FiMic,
      color: "from-purple-500 to-pink-500",
      border: "border-purple-500/30",
      link: "/services/press-releases"
    },
    {
      title: "YouTube",
      desc: "Boost your YouTube presence and reach the right audience",
      icon: FaIcons.FaYoutube,
      color: "from-red-600 to-red-700",
      border: "border-red-600/30",
      link: "/services/youtube"
    },
    {
      title: "Reddit",
      desc: "Drive real discussions and brand mentions in targeted communities",
      icon: FaIcons.FaReddit,
      color: "from-orange-400 to-red-500",
      border: "border-orange-500/30",
      link: "/services/reddit"
    },
    {
      title: "Link Insertions",
      desc: "Place your link inside existing articles that already rank on Google",
      icon: FiIcons.FiLink2,
      color: "from-emerald-400 to-teal-500",
      border: "border-emerald-500/30",
      link: "/services/link-insertions"
    },
    {
      title: "Brand Mention",
      desc: "Get your brand mentioned on relevant websites to increase trust, visibility, and authority",
      icon: FiIcons.FiAward,
      color: "from-pink-500 to-rose-500",
      border: "border-pink-500/30",
      link: "/services/brand-mentions"
    },
    {
      title: "Quora",
      desc: "Rank answers and capture long-tail search traffic",
      icon: FaIcons.FaQuora,
      color: "from-red-500 to-rose-600",
      border: "border-red-500/30",
      link: "/services/quora"
    },
    {
      title: "Google Maps",
      desc: "Rank #1 on Google Maps for local businesses.",
      icon: FaIcons.FaGoogle,
      color: "from-indigo-500 to-blue-600",
      border: "border-indigo-500/30",
      link: "/services/google-maps"
    },
    {
      title: "Product Reviews",
      desc: "Get your product or service featured in relevant review articles",
      icon: FiIcons.FiStar,
      color: "from-yellow-400 to-amber-500",
      border: "border-yellow-500/30",
      link: "/services/product-reviews"
    }
  ];

  return (
    <section className="py-24 relative" id="services">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading 
          headline="Everything You Need to Build Authority Online"
          subheadline="From press releases to backlinks and brand placements, access the strategies that help your business rank and get discovered"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10 mt-12">
          {services.map((service, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className={`glass-card rounded-2xl border ${service.border} hover:bg-slate-800/80 transition-all group hover:scale-[1.02] cursor-pointer overflow-hidden`}
            >
              <Link to={service.link} className="flex flex-col w-full h-full p-8">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} p-0.5 mb-6 shadow-lg`}>
                  <div className="w-full h-full bg-slate-900 rounded-[10px] flex items-center justify-center">
                    <SafeIcon icon={service.icon} className="text-2xl text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-slate-300 transition-all">
                  {service.title}
                </h3>
                
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  {service.desc}
                </p>
                
                <div className="flex items-center text-sm font-medium text-blue-400 group-hover:text-blue-300 mt-auto">
                  Learn More <SafeIcon icon={FiIcons.FiArrowRight} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Enlarged Buttons Section */}
        <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6">
          <Button 
            variant="primary" 
            className="!px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(59,130,246,0.3)] w-full sm:w-auto"
            onClick={() => navigate('/contact')}
          >
            Contact us
          </Button>
          <Button 
            variant="secondary" 
            className="!px-12 !py-5 text-xl font-bold w-full sm:w-auto group"
            onClick={openCalendly}
          >
            Book a call
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;